import { type InitParticipantDto } from 'app/components/participants/types';
import { ParticipantDto } from 'data';

export { setupEventSource } from './setup-event-source';
export * from './dars';
export { downloadFileFromCollection, download } from './download';
export * from './transactions-history';
export * from './date-parsers';
export * from './check-enterprise-cron';
export * from './resource-updaters';
export * from './capitalize';
export * from './storage-updaters';
export * from './select-input-adapters';
export * from './is-empty';
export * from './format-boolean-for-details-page';
export * from './get-next-run-date';
export * from './party-select-adapter';
export * from './local-parties-error';
export * from './convert-to-query-string';
export * from './create-nested-object';
export * from './get-public-app-url';
export * from './table-state';
export * from './are-filters-applied';
export * from './pagination-storage';
export * from './top-up';
export * from './get-env-default';
export * from './kms-defaults';
export * from './compare-semver';
export * from './validate-hocon';

export const formatDate = (
  date: Date,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
): string => {
  const formatter = new Intl.DateTimeFormat(locale, options);
  return formatter.format(date);
};

export const secondsToDHMS = (sec: string) => {
  const seconds = Number(sec);
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const dDisplay = d > 0 ? `${d}d ` : '';
  const hDisplay = h > 0 ? `${h}h ` : '';
  const mDisplay = m > 0 ? `${m}m ` : '';
  const sDisplay = s > 0 ? `${s}sec` : '';

  if (d > 0) {
    return dDisplay + hDisplay + mDisplay;
  }

  return hDisplay + mDisplay + sDisplay;
};

export const handleIntegerInput = (e) => {
  const keyCode = e.keyCode || e.which;
  const keyValue = String.fromCharCode(keyCode);
  if (/[0-9]|\./.test(keyValue)) return;
  e.preventDefault();
};

export const handleNonNegativeIntegerInput = (e) => {
  const keyCode = e.keyCode || e.which;
  const keyValue = String.fromCharCode(keyCode);
  if (!/[0-9]/.test(keyValue)) {
    e.preventDefault();
    return;
  }

  const target = e.target as HTMLInputElement | null;
  if (!target) return;

  const { value, selectionStart, selectionEnd } = target;
  const start = selectionStart ?? value.length;
  const end = selectionEnd ?? value.length;
  const nextValue = value.slice(0, start) + keyValue + value.slice(end);

  if (/^0\d/.test(nextValue)) {
    e.preventDefault();
  }
};

export const parseNonNegativeIntegerInput = (valueOrEvent: unknown): number | '' => {
  let raw: string;
  if (typeof valueOrEvent === 'object' && valueOrEvent !== null && 'target' in valueOrEvent) {
    raw = String((valueOrEvent as { target: { value?: unknown } }).target?.value ?? '');
  } else {
    raw = String(valueOrEvent ?? '');
  }
  if (raw === '') return '';

  const normalized = raw.replace(/^0+(?=\d)/, '');
  if (normalized === '') return 0;

  const parsed = Number.parseInt(normalized, 10);
  return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
};

export const handleCpuResourcesInput = (e) => {
  const keyCode = e.keyCode || e.which;
  const keyValue = String.fromCharCode(keyCode);
  if (/[0-9]|\.|m/.test(keyValue)) return;
  e.preventDefault();
};

export const prepareEnvVarsToRequest = (
  envVars: InitParticipantDto['envVars'] = [],
  postgresVars: InitParticipantDto['envVars'] = [],
): ParticipantDto['envVars'] => [...postgresVars, ...envVars];

export const prepareEnvVarsToForm = (
  envVars?: ParticipantDto['envVars'],
):
  | {
      preparedEnvVars: InitParticipantDto['envVars'];
      postgresVars: InitParticipantDto['envVars'];
    }
  | undefined => {
  if (!envVars) return envVars;

  const postgresVarRegExp = /\b(POSTGRES_USER|POSTGRES_PASSWORD)\b/;

  return {
    preparedEnvVars: envVars.filter((env) => !postgresVarRegExp.test(env.name as string)),
    postgresVars: envVars.filter((env) => postgresVarRegExp.test(env.name as string)),
  };
};
